import { versionSetAside, type TurnVersionsLookup } from './turnVersions';

/**
 * Whether "Fix with AI" may repair the crashed version in place (test finding 5).
 *
 * The repair is a build of that version's own turn, and a build edits the prototype's files on
 * Chef, which hold its newest version. So only the newest version of its prototype is repaired
 * in place; an older one would get the fix applied to newer files. Anything else keeps the chat
 * message, which Nexus plans as a new change.
 *
 * `turnIds` are the conversation's prototype turns, oldest first. With the versions known, the
 * newest turn of the active version's prototype decides; without them (or a version not attached
 * to a prototype yet), the conversation's newest prototype turn.
 */
export function canRepairInPlace(args: {
  activeTurnId: string | null | undefined;
  turnIds: readonly string[];
  turnVersions?: TurnVersionsLookup;
}): boolean {
  const { activeTurnId, turnIds, turnVersions } = args;
  if (!activeTurnId || !turnIds.includes(activeTurnId)) return false;

  const active = turnVersions?.status === 'ready' ? turnVersions.get(activeTurnId) : undefined;
  if (!active?.prototypeId) return turnIds[turnIds.length - 1] === activeTurnId;

  const sameProtoTurns = turnIds.filter((id) => {
    const version = turnVersions!.get(id);
    // A build that was removed or replaced is not one of the prototype's versions any more.
    return version?.prototypeId === active.prototypeId && !versionSetAside(version?.status);
  });
  return sameProtoTurns[sameProtoTurns.length - 1] === activeTurnId;
}
